/**
 * Whether this browser can actually deliver a sitting.
 *
 * The real test day shows a candidate "your browser has been approved" before
 * they are let in, and the point of saying it is that it was checked. A tick
 * that appears whatever the browser can do would be worse than no tick at all:
 * a candidate would take it as a promise and discover otherwise once the clock
 * was running.
 *
 * So these are the things a sitting genuinely needs, and each is asked of the
 * browser rather than assumed.
 */

export interface CheckResult {
  ok: boolean;
  failures: string[];
}

/** `canPlayType` answers '', 'maybe' or 'probably'; anything but '' will do. */
function canPlay(el: HTMLMediaElement, types: string[]): boolean {
  return types.some((t) => el.canPlayType(t) !== '');
}

export function checkBrowser(): CheckResult {
  const failures: string[] = [];

  const audio = document.createElement('audio');
  if (!canPlay(audio, ['audio/mpeg', 'audio/mp4', 'audio/wav'])) {
    failures.push('This browser cannot play the listening recordings.');
  }

  const video = document.createElement('video');
  if (!canPlay(video, ['video/mp4', 'video/webm'])) {
    failures.push('This browser cannot play the instruction films.');
  }

  // The sitting keeps the chosen centre and other small state here, and a
  // browser with storage blocked loses it on every navigation.
  try {
    const probe = '__target_probe__';
    sessionStorage.setItem(probe, '1');
    sessionStorage.removeItem(probe);
  } catch {
    failures.push('This browser is blocking site storage, which the test needs.');
  }

  return { ok: failures.length === 0, failures };
}
